import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  linkedSignal,
  output,
  viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

import { PlanEditGesture } from '@core/models/plan-edit-gesture.model';
import { ICONS } from '@core/services/icons/icons.service';
import { PlanEditSessionActions } from '@core/store/plan-edit/plan-edit-session.actions';
import {
  createDateTimeWithTz,
  previousIsoDate,
  toNaiveDateInTz,
  toNaiveTimeInTz
} from '@core/utils/time-utils';

import { RouteStartRequest, SetTimeRequest } from '../plan-edit-forms.service';

function nextIsoDate(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10);
}

/**
 * The instant `time` names in `timeZone` on the day nearest `around`: a field holding only a time
 * reads 00:30 typed on a 23:50 stop as 40 minutes later, not 23 hours earlier.
 */
function nearestInstant(time: string, timeZone: string, around: Date): Date {
  const day = toNaiveDateInTz(around, timeZone);
  return [previousIsoDate(day), day, nextIsoDate(day)]
    .map((candidate) => createDateTimeWithTz(candidate, `${time}:00`, timeZone))
    .reduce((best, candidate) =>
      Math.abs(candidate.getTime() - around.getTime()) <
      Math.abs(best.getTime() - around.getTime())
        ? candidate
        : best
    );
}

const SET_TIME_KEYS = {
  title: 'features.plan.edit.set_time.title',
  start: 'features.plan.edit.set_time.start',
  note: 'features.plan.edit.set_time.note',
  release: 'features.plan.edit.set_time.release',
  pin: 'features.plan.edit.set_time.pin'
};
const ROUTE_START_KEYS: typeof SET_TIME_KEYS = {
  title: 'features.plan.edit.route_start.title',
  start: 'features.plan.edit.route_start.start',
  note: 'features.plan.edit.route_start.note',
  release: 'features.plan.edit.route_start.release',
  pin: 'features.plan.edit.route_start.set'
};

/**
 * Pins a stop, or sets when a route leaves, at a time typed in the site's zone; or releases it.
 */
@Component({
  selector: 'app-set-time-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
  templateUrl: './set-time-form.component.html',
  styles: ``
})
export class SetTimeFormComponent {
  readonly request = input.required<SetTimeRequest | RouteStartRequest>();
  readonly done = output<void>();

  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly timeInput =
    viewChild.required<ElementRef<HTMLInputElement>>('timeInput');

  protected readonly ICONS = ICONS;
  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(map((change) => change.lang)),
    { initialValue: this.translate.getCurrentLang() }
  );

  /** What the form is titled after: the stop, or the route whose start it sets. */
  protected readonly name = computed(() => {
    const request = this.request();
    return request.kind === 'set-time' ? request.stopName : request.routeName;
  });
  /** The translation keys of the words that differ between a stop's time and a route's start. */
  protected readonly keys = computed(() =>
    this.request().kind === 'set-time' ? SET_TIME_KEYS : ROUTE_START_KEYS
  );

  private readonly current = computed(() => new Date(this.request().current));
  private readonly shown = computed(() =>
    toNaiveTimeInTz(this.current(), this.request().siteTimeZone).slice(0, 5)
  );
  protected readonly time = linkedSignal(() => this.shown());
  /** The time as shown pins the stop's exact start, seconds the field cannot show included. */
  protected readonly at = computed(() => {
    const time = this.time();
    if (time === this.shown()) return this.current();
    return /^\d{2}:\d{2}$/.test(time)
      ? nearestInstant(time, this.request().siteTimeZone, this.current())
      : null;
  });
  protected readonly dayLabel = computed(() => {
    const at = this.at();
    if (!at) return '';
    return new Intl.DateTimeFormat(this.currentLang() ?? undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: this.request().siteTimeZone
    }).format(at);
  });

  constructor() {
    afterNextRender(() => this.timeInput().nativeElement.focus());
  }

  protected onTimeInput(event: Event): void {
    this.time.set((event.target as HTMLInputElement).value);
  }

  protected pin(): void {
    const at = this.at();
    if (!at) return;
    this.send(this.gestureAt(at.toISOString()));
  }

  protected release(): void {
    this.send(this.gestureAt(null));
  }

  private gestureAt(at: string | null): PlanEditGesture {
    const request = this.request();
    if (request.kind === 'route-start') {
      return { kind: 'set-route-start', routeUid: request.routeUid, at };
    }
    const { routeUid, actionUid } = request;
    return at === null
      ? { kind: 'release-pin', routeUid, actionUid }
      : { kind: 'pin', routeUid, actionUid, at };
  }

  private send(gesture: PlanEditGesture): void {
    this.store.dispatch(PlanEditSessionActions.gestureRequested({ gesture }));
    this.done.emit();
  }
}
